import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import ChessBoardView from '@/components/chess/ChessBoardView';
import useChessEngine from '@/hooks/useChessEngine';
import { playChessMoveSound, playChessCaptureSound } from '@/lib/chessSounds';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Bot, CheckCircle2, Clock, Loader2, Repeat, Sparkles, Trophy } from 'lucide-react';

// FASE QUIZZES · PASO 5C-2 — Runner de UNA actividad tipo `play_computer`
// dentro del Quiz del alumno.
//
// CONTRATO (igual que QuizPuzzleRunner para que QuizStart no tenga que ramificar):
//   props: { activity, position, totalActivities, quizTitle,
//            maxPoints, maxAttempts, initialAttemptsUsed,
//            onCompleted, onAttemptFailed }
//
// REGLAS 5C-2:
//   · Fuente = activity_snapshots[position]. NO consulta Study.
//   · 1 partida completa = 1 intento (no por movimiento).
//   · Mate del alumno → points_earned = maxPoints (SIN aplicar 30/20/10).
//   · Derrota o tablas con intentos restantes → onAttemptFailed y "Intentar de nuevo".
//   · Derrota o tablas EN el último intento → onCompleted con 0 pts (avance automático).
//   · No se llama a /studies/.../play-computer/attempt. La persistencia vive
//     en QuizStart → POST /quiz-assignments/{aid}/progress.

// Escudo mínimo contra FEN vacíos/invalidos. `chess.js` lanza en constructor.
function tryLoad(fen) {
  if (!fen || typeof fen !== 'string' || !fen.trim()) return null;
  try { return new Chess(fen); } catch { return null; }
}

function fmtTime(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function Confetti() {
  const pieces = React.useMemo(() => {
    const colors = ['#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6', '#ef4444', '#facc15'];
    return Array.from({ length: 48 }, (_, i) => ({
      left: Math.random() * 100, delay: Math.random() * 0.5,
      duration: 1.4 + Math.random() * 1.4, rotate: Math.random() * 360,
      color: colors[i % colors.length], size: 6 + Math.random() * 6,
    }));
  }, []);
  return (
    <div data-testid="qpc-confetti" className="pointer-events-none absolute inset-0 overflow-hidden">
      <style>{`@keyframes qpc-fall {0%{transform:translateY(-20px) rotate(0);opacity:1}100%{transform:translateY(560px) rotate(720deg);opacity:0}}`}</style>
      {pieces.map((p, i) => (
        <span key={i} style={{
          position: 'absolute', top: -20, left: `${p.left}%`, width: p.size, height: p.size,
          background: p.color, borderRadius: 2, transform: `rotate(${p.rotate}deg)`,
          animation: `qpc-fall ${p.duration}s ${p.delay}s ease-in forwards`,
        }} />
      ))}
    </div>
  );
}

export default function QuizPlayComputerRunner({
  activity, position, totalActivities, quizTitle,
  maxPoints = 30, maxAttempts = 3,
  initialAttemptsUsed = 0,
  onCompleted, onAttemptFailed,
}) {
  const engine = useChessEngine();
  const gameRef = useRef(null);
  const startedRef = useRef(Date.now());
  const completedFiredRef = useRef(false);
  const attemptSubmittedRef = useRef(false); // evita procesar dos veces el mismo fin de partida
  // Layout-only: ancho del tablero medido del contenedor real (ResizeObserver),
  // evita overflow en tablet (768px) donde el sidebar reduce el ancho útil.
  const boardWrapRef = useRef(null);
  const [boardPx, setBoardPx] = useState(480);
  useEffect(() => {
    const el = boardWrapRef.current;
    if (!el) return;
    const measure = () => { const w = el.clientWidth || 0; if (w > 0) setBoardPx(Math.max(200, Math.min(560, Math.floor(w)))); };
    measure();
    let ro; if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  // Config estable derivada del snapshot
  const initialFen = activity?.fen || '';
  const studentSide = activity?.student_side === 'black' ? 'b' : 'w';
  const engineSide = studentSide === 'w' ? 'b' : 'w';
  const orientation = studentSide === 'w' ? 'white' : 'black';
  const level = Number.isFinite(activity?.engine_level) ? activity.engine_level : 5;
  const timeLimit = Number.isFinite(activity?.time_seconds) && activity.time_seconds > 0
    ? Number(activity.time_seconds) : 0;

  // Estado de la partida en curso
  const [fen, setFen] = useState('');
  const [status, setStatus] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [attemptsUsed, setAttemptsUsed] = useState(Math.max(0, Math.min(maxAttempts - 1, initialAttemptsUsed || 0)));
  const [result, setResult] = useState(null); // 'win' | 'loss' | 'draw' | 'timeout'
  const [locked, setLocked] = useState(false);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalDests, setLegalDests] = useState({});
  const [seconds, setSeconds] = useState(0);
  const [remaining, setRemaining] = useState(timeLimit || 0);

  const currentAttempt = attemptsUsed + 1; // partida en curso (1..maxAttempts)
  const attemptsLeftAfterCurrent = maxAttempts - currentAttempt; // intentos disponibles DESPUÉS de este

  // Reset completo al montar / cambiar de actividad.
  const initGame = useCallback(() => {
    const c = tryLoad(initialFen);
    if (!c) { gameRef.current = null; setInvalid(true); return; }
    gameRef.current = c;
    setFen(c.fen());
    setInvalid(false);
    setResult(null);
    setSelectedSquare(null);
    setLegalDests({});
    attemptSubmittedRef.current = false;
    startedRef.current = Date.now();
    setSeconds(0);
    setRemaining(timeLimit || 0);
    setStatus(c.turn() === studentSide ? 'Tu turno' : 'Calculando…');
  }, [initialFen, studentSide, timeLimit]);

  useEffect(() => {
    completedFiredRef.current = false;
    setLocked(false);
    setAttemptsUsed(Math.max(0, Math.min(maxAttempts - 1, initialAttemptsUsed || 0)));
    initGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.id, activity?.source_activity_id]);

  // Timer informativo global (para elapsed_seconds).
  useEffect(() => {
    const iv = setInterval(() => setSeconds(Math.floor((Date.now() - startedRef.current) / 1000)), 500);
    return () => clearInterval(iv);
  }, []);

  // Countdown de partida (si activity.time_seconds > 0).
  useEffect(() => {
    if (!timeLimit || locked || result || invalid) return;
    if (remaining <= 0) return;
    const iv = setInterval(() => {
      setRemaining(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(iv);
  }, [timeLimit, locked, result, invalid, remaining]);

  // Detectar fin de partida (mate o tablas) tras cada jugada.
  const evaluateEnd = useCallback(() => {
    const c = gameRef.current;
    if (!c) return false;
    try {
      if (typeof c.isCheckmate === 'function' && c.isCheckmate()) {
        const loser = c.turn(); // el que debía mover perdió
        setResult(loser === studentSide ? 'loss' : 'win');
        setStatus('');
        return true;
      }
      if (typeof c.isDraw === 'function' && c.isDraw()) {
        setResult('draw');
        setStatus('');
        return true;
      }
    } catch { /* ignore */ }
    return false;
  }, [studentSide]);

  const finishActivity = useCallback((success, attemptsAtFinish) => {
    if (completedFiredRef.current) return;
    completedFiredRef.current = true;
    setLocked(true);
    try {
      onCompleted?.({
        activity_id: activity?.source_activity_id || activity?.id,
        position,
        attempts_used: attemptsAtFinish,
        points_earned: success ? (Number.isFinite(maxPoints) ? maxPoints : 0) : 0,
        elapsed_seconds: Math.floor((Date.now() - startedRef.current) / 1000),
        success,
        completed: true,
      });
    } catch { /* silent — el padre no debe romper el runner */ }
  }, [activity, position, onCompleted, maxPoints]);

  const registerFailedAttempt = useCallback((attemptsAtFinish) => {
    // attemptsAtFinish = número de partidas completadas (1..maxAttempts).
    if (attemptsAtFinish >= maxAttempts) {
      // Último intento fallido — la actividad se cierra con 0 puntos.
      toast('✨ ¡Vamos con el siguiente reto!', { duration: 1400 });
      setTimeout(() => finishActivity(false, maxAttempts), 500);
      return;
    }
    // Aún quedan intentos: persistir attempts_used parcial y esperar botón "Intentar de nuevo".
    toast('💪 ¡Vamos de nuevo!', { duration: 1400 });
    try {
      onAttemptFailed?.({
        activity_id: activity?.source_activity_id || activity?.id,
        position,
        attempts_used: attemptsAtFinish,
        completed: false,
        elapsed_seconds: Math.floor((Date.now() - startedRef.current) / 1000),
      });
    } catch { /* silent */ }
  }, [maxAttempts, finishActivity, onAttemptFailed, activity, position]);

  // Cuando llega un resultado, contabilizamos el intento UNA sola vez.
  useEffect(() => {
    if (!result || attemptSubmittedRef.current) return;
    attemptSubmittedRef.current = true;
    const next = attemptsUsed + 1;
    setAttemptsUsed(next);
    if (result === 'win') {
      finishActivity(true, next);
    } else {
      registerFailedAttempt(next);
    }
  }, [result, attemptsUsed, finishActivity, registerFailedAttempt]);

  // Timeout → derrota del alumno con las mismas reglas.
  useEffect(() => {
    if (!timeLimit || locked || result || invalid) return;
    if (remaining === 0) {
      setResult('timeout');
    }
  }, [remaining, timeLimit, locked, result, invalid]);

  const playEngineMove = useCallback(async () => {
    const c = gameRef.current;
    if (!c || !engine.ready || result || locked) return;
    if (c.turn() !== engineSide) return;
    setStatus('Calculando…');
    try {
      const uci = await engine.bestMove(c.fen(), { level, movetime: 700 });
      if (!uci || uci === '(none)') {
        if (!evaluateEnd()) setStatus('');
        return;
      }
      const from = uci.slice(0, 2), to = uci.slice(2, 4), promo = uci.length > 4 ? uci[4] : 'q';
      let mv = null;
      try { mv = c.move({ from, to, promotion: promo }); } catch { mv = null; }
      if (!mv) { setStatus(''); return; }
      setFen(c.fen());
      (mv.captured ? playChessCaptureSound : playChessMoveSound)();
      if (evaluateEnd()) return;
      setStatus(c.turn() === studentSide ? 'Tu turno' : '');
    } catch {
      setStatus('');
    }
  }, [engine, engineSide, level, studentSide, result, locked, evaluateEnd]);

  // Trigger del motor cuando le toca.
  useEffect(() => {
    if (!engine.ready || !gameRef.current || result || locked || invalid) return;
    if (gameRef.current.turn() === engineSide && !engine.thinking) {
      playEngineMove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine.ready, fen, result, locked, invalid]);

  const handleDrop = useCallback((sourceSquare, targetSquare) => {
    const c = gameRef.current;
    if (!c || locked || result || invalid) return false;
    if (c.turn() !== studentSide) return false;
    let mv = null;
    try { mv = c.move({ from: sourceSquare, to: targetSquare, promotion: 'q' }); } catch { mv = null; }
    if (!mv) return false;
    setFen(c.fen());
    setSelectedSquare(null); setLegalDests({});
    if (evaluateEnd()) return true;
    setStatus('');
    setTimeout(() => playEngineMove(), 100);
    return true;
  }, [locked, result, invalid, studentSide, evaluateEnd, playEngineMove]);

  // Click-to-move (mismo patrón que QuizPuzzleRunner/PlayComputerPlayer).
  const computeLegalDests = useCallback((sq) => {
    const c = gameRef.current;
    if (!c) return {};
    try {
      const mv = c.moves({ square: sq, verbose: true });
      const dests = {};
      mv.forEach(m => { dests[m.to] = { isCapture: !!(m.flags && (m.flags.includes('c') || m.flags.includes('e'))) }; });
      return dests;
    } catch { return {}; }
  }, []);

  const handleSquareClick = useCallback((sq) => {
    const c = gameRef.current;
    if (!c || locked || result || invalid) return;
    if (c.turn() !== studentSide) return;
    if (selectedSquare) {
      if (sq === selectedSquare) { setSelectedSquare(null); setLegalDests({}); return; }
      if (legalDests[sq]) {
        let wasCapture = false;
        try {
          const dstPiece = c.get(sq);
          if (dstPiece) wasCapture = true;
          else {
            const srcPiece = c.get(selectedSquare);
            if (srcPiece && srcPiece.type === 'p' && selectedSquare[0] !== sq[0]) wasCapture = true;
          }
        } catch { /* silent */ }
        const ok = handleDrop(selectedSquare, sq);
        if (ok) (wasCapture ? playChessCaptureSound : playChessMoveSound)();
        return;
      }
      const p = c.get(sq);
      if (p && p.color === studentSide) {
        setSelectedSquare(sq); setLegalDests(computeLegalDests(sq)); return;
      }
      setSelectedSquare(null); setLegalDests({});
      return;
    }
    const p = c.get(sq);
    if (!p || p.color !== studentSide) return;
    const d = computeLegalDests(sq);
    if (Object.keys(d).length === 0) return;
    setSelectedSquare(sq); setLegalDests(d);
  }, [locked, result, invalid, studentSide, selectedSquare, legalDests, handleDrop, computeLegalDests]);

  const hintStyles = useMemo(() => {
    const s = {};
    if (selectedSquare) s[selectedSquare] = { backgroundColor: 'rgba(255, 236, 100, 0.75)' };
    Object.entries(legalDests).forEach(([dst, meta]) => {
      const base = s[dst] || {};
      s[dst] = {
        ...base,
        background: meta.isCapture
          ? 'radial-gradient(circle, transparent 0 88%, rgba(0,0,0,0.42) 88% 96%, transparent 96% 100%)'
          : 'radial-gradient(circle, rgba(0,0,0,0.28) 0 18%, transparent 18% 100%)',
      };
    });
    return s;
  }, [selectedSquare, legalDests]);

  const handleRetry = useCallback(() => {
    if (locked || completedFiredRef.current) return;
    if (attemptsUsed >= maxAttempts) return;
    initGame();
  }, [locked, attemptsUsed, maxAttempts, initGame]);

  // -------- Render --------

  if (!activity) return null;

  // Estado friendly cuando la actividad viene sin FEN válido: no consume intento
  // y no llama al backend. QuizStart no avanza — el alumno puede volver.
  if (invalid) {
    return (
      <div data-testid="qpc-invalid" className="min-h-[calc(100vh-4rem)] grid place-items-center p-6 bg-gradient-to-b from-indigo-50/40 via-white to-white">
        <div className="max-w-lg text-center rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
          <div className="text-5xl mb-3">♟️</div>
          <div className="font-display font-black text-xl text-slate-900">Esta actividad no tiene una posición válida.</div>
          <div className="text-sm text-slate-600 mt-2">Avísale a tu profesor para que la revise.</div>
        </div>
      </div>
    );
  }

  const pct = totalActivities > 0 ? Math.round(((position) / totalActivities) * 100) : 0;
  const instruction = (activity?.instructions || activity?.title || 'Consigue dar jaque mate.').toString();
  const sideLabel = studentSide === 'w' ? 'Juega con blancas' : 'Juega con negras';

  const canRetry = !!result && !locked && attemptsUsed < maxAttempts;

  // Banner de resultado con lenguaje siempre positivo.
  const banner = result === 'win'
    ? { testId: 'qpc-result-win', title: '🏆 ¡Jaque mate!', sub: `¡Excelente! Has completado esta actividad.`, cls: 'bg-emerald-50 border-emerald-200 text-emerald-800', celebrate: true }
    : result === 'draw'
      ? { testId: 'qpc-result-draw', title: '✨ ¡Vamos de nuevo!', sub: 'La partida terminó en tablas.', cls: 'bg-amber-50 border-amber-200 text-amber-800' }
      : (result === 'loss' || result === 'timeout')
        ? (attemptsUsed >= maxAttempts
          ? { testId: 'qpc-result-final', title: '✨ ¡Vamos con el siguiente reto!', sub: 'Sigue practicando: cada partida te hace mejor.', cls: 'bg-slate-50 border-slate-200 text-slate-800' }
          : { testId: 'qpc-result-loss', title: '💪 ¡Vamos de nuevo!', sub: '¡Aún tienes otro intento!', cls: 'bg-indigo-50 border-indigo-200 text-indigo-800' })
        : null;

  return (
    <div data-testid="qpc-runner" className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-indigo-50/40 via-white to-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Header */}
        <div className="mb-4">
          <div data-testid="qpc-title" className="font-display font-black text-xl sm:text-2xl text-slate-900 truncate">🎯 {quizTitle}</div>
          <div className="flex items-center justify-between mt-1 gap-3">
            <div data-testid="qpc-progress-text" className="text-xs sm:text-sm text-slate-600 font-semibold">Actividad {position + 1} de {totalActivities}</div>
            <div className="flex items-center gap-2">
              {timeLimit > 0 && (
                <div data-testid="qpc-countdown" className={`inline-flex items-center gap-1 text-xs sm:text-sm font-mono ${remaining <= 10 ? 'text-rose-600' : 'text-slate-500'}`}>
                  <Clock className="w-3.5 h-3.5" /> {fmtTime(remaining)}
                </div>
              )}
              <div data-testid="qpc-timer" className="inline-flex items-center gap-1 text-xs sm:text-sm font-mono text-slate-400">
                {timeLimit > 0 ? null : <Clock className="w-3.5 h-3.5" />} {timeLimit > 0 ? '' : fmtTime(seconds)}
              </div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden mt-2">
            <div data-testid="qpc-progress-bar" className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* Instrucción */}
        <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-indigo-600 font-bold flex items-center gap-1"><Sparkles className="w-3 h-3" /> Instrucción</div>
              <div data-testid="qpc-instruction" className="font-display font-extrabold text-base sm:text-lg text-slate-900 mt-0.5 truncate">{instruction}</div>
              <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1"><Bot className="w-3.5 h-3.5 text-indigo-500"/> ♟️ {sideLabel}</div>
            </div>
            <div data-testid="qpc-attempt-badge" className="text-[11px] text-slate-500 font-semibold whitespace-nowrap ml-3">
              Intento {Math.min(currentAttempt, maxAttempts)} de {maxAttempts}
            </div>
          </div>
        </div>

        {/* Estado del motor + banner de resultado */}
        <div className="rounded-2xl bg-white border border-slate-200 p-3 sm:p-4">
          <div className="flex items-center justify-between mb-2 px-1 text-xs text-slate-500">
            <div className="inline-flex items-center gap-1">
              {!engine.ready ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando motor…</>
              ) : engine.thinking ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> <span data-testid="qpc-status">Calculando…</span></>
              ) : (
                <span data-testid="qpc-status">{result ? 'Partida finalizada' : status}</span>
              )}
            </div>
            {canRetry && (
              <Button
                data-testid="qpc-retry"
                size="sm"
                onClick={handleRetry}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Repeat className="w-3.5 h-3.5 mr-1" /> Intentar de nuevo · te queda{attemptsLeftAfterCurrent === 1 ? '' : 'n'} {attemptsLeftAfterCurrent} intento{attemptsLeftAfterCurrent === 1 ? '' : 's'}
              </Button>
            )}
          </div>

          {banner && (
            <div data-testid={banner.testId} className={`mb-3 rounded-xl border p-3 flex items-center gap-3 ${banner.cls}`}>
              {banner.celebrate ? <Trophy className="w-6 h-6 shrink-0" /> : <Sparkles className="w-6 h-6 shrink-0" />}
              <div className="min-w-0">
                <div data-testid="qpc-result-title" className="font-display font-extrabold text-base leading-tight">{banner.title}</div>
                <div data-testid="qpc-result-sub" className="text-xs mt-0.5">{banner.sub}</div>
              </div>
            </div>
          )}

          <div ref={boardWrapRef} data-testid="qpc-board" className="w-full max-w-[560px] mx-auto relative">
            <ChessBoardView
              fen={fen}
              onPieceDrop={handleDrop}
              onSquareClick={handleSquareClick}
              extraSquareStyles={hintStyles}
              orientation={orientation}
              boardWidth={boardPx}
              arePiecesDraggable={engine.ready && !result && !locked}
            />
            {banner?.celebrate && <Confetti />}
          </div>
        </div>

        {/* Estado bloqueado (paso a la siguiente actividad lo dispara QuizStart) */}
        {locked && (
          <div data-testid="qpc-completed" className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 p-4 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 shrink-0" />
            <div>
              <div className="font-display font-extrabold">Actividad completada</div>
              <div className="text-xs text-emerald-700">Preparando el siguiente reto…</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
